import React, { useState, useMemo, useCallback } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLinkedin } from '@fortawesome/free-brands-svg-icons'
import { faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { X } from 'lucide-react'
import type {
    Candidate,
    Rubric,
    RubricSection,
    RubricCriterion,
    ScoreType,
    ScoreValue,
} from '../types'
import { normaliseScore } from '../types'
import RubricScoreBadge from './RubricScoreBadge'
import ColumnFilter from './ColumnFilter'

// ---------------------------------------------------------------------------
// Score helpers
// ---------------------------------------------------------------------------

function scoreToNumeric(
    value: ScoreValue,
    scoreType: ScoreType,
): { actual: number; max: number } {
    if (value === null) return { actual: 0, max: 0 }
    if (value === 'not_asked') return { actual: 0, max: 0 }
    if (value === 'not_answered') return { actual: 0, max: 1 }
    if (scoreType === 'numeric-1-3') {
        return { actual: value as number, max: 3 }
    }
    // binary and pass-flag-fail
    if (value === 'pass') return { actual: 1, max: 1 }
    if (value === 'flag') return { actual: 0.5, max: 1 }
    return { actual: 0, max: 1 } // fail
}

function calculateScorePercent(
    candidate: Candidate,
    allCriteria: { criterion: RubricCriterion }[],
): number {
    // Use backend-computed overall percentage when available
    if (candidate.overallPercentage !== null) {
        return Math.round(candidate.overallPercentage)
    }
    // Fallback to client-side calculation
    let totalActual = 0
    let totalMax = 0
    for (const { criterion } of allCriteria) {
        const entry = normaliseScore(candidate.scores[criterion.id])
        const { actual, max } = scoreToNumeric(entry.value, criterion.scoreType)
        totalActual += actual
        totalMax += max
    }
    return totalMax === 0 ? 0 : Math.round((totalActual / totalMax) * 100)
}

// ---------------------------------------------------------------------------
// Filter state types
// ---------------------------------------------------------------------------

type NameFilter = { value: string }
type ScoreFilter = { operator: '>=' | '<=' | '='; value: number }
type CriterionFilter = { selected: Set<string> }

type FilterState = {
    name: NameFilter | null
    score: ScoreFilter | null
    criteria: Record<string, CriterionFilter>
}

const EMPTY_FILTERS: FilterState = {
    name: null,
    score: null,
    criteria: {},
}

// ---------------------------------------------------------------------------
// Discrete option helpers
// ---------------------------------------------------------------------------

function optionsForScoreType(
    scoreType: ScoreType,
): { label: string; value: string }[] {
    const common = [
        { label: 'Not Asked', value: 'not_asked' },
        { label: 'Not Answered', value: 'not_answered' },
    ]
    if (scoreType === 'binary') {
        return [
            { label: 'Pass', value: 'pass' },
            { label: 'Fail', value: 'fail' },
            ...common,
        ]
    }
    if (scoreType === 'pass-flag-fail') {
        return [
            { label: 'Pass', value: 'pass' },
            { label: 'Flag', value: 'flag' },
            { label: 'Fail', value: 'fail' },
            ...common,
        ]
    }
    // numeric-1-3
    return [
        { label: 'Strong (3)', value: '3' },
        { label: 'Clear (2)', value: '2' },
        { label: 'Touched (1)', value: '1' },
        ...common,
    ]
}

function scoreValueToFilterKey(value: ScoreValue): string {
    if (value === null || value === undefined) return '__null__'
    return String(value)
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
    rubric: Rubric
    candidates: Candidate[]
}

/**
 * Section colour palette: each rubric section gets a distinct left-border
 * accent and subtle header background to visually group its criteria columns.
 */
const SECTION_ACCENTS: {
    headerBg: string
    borderColor: string
}[] = [
    {
        headerBg: 'bg-blue-50/80',
        borderColor: 'border-l-blue-300',
    },
    {
        headerBg: 'bg-slate-50/80',
        borderColor: 'border-l-slate-300',
    },
    {
        headerBg: 'bg-amber-50/70',
        borderColor: 'border-l-amber-300',
    },
    {
        headerBg: 'bg-emerald-50/70',
        borderColor: 'border-l-emerald-300',
    },
]

export default function CandidatesTable({
    rubric,
    candidates,
}: Props): React.JSX.Element {
    const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)

    // Flatten all criteria with section index for colour coding
    const allCriteria: {
        section: RubricSection
        sectionIndex: number
        criterion: RubricCriterion
        isFirstInSection: boolean
        isLastInSection: boolean
    }[] = rubric.sections.flatMap((section, sectionIndex) =>
        section.criteria.map((criterion, criterionIndex) => ({
            section,
            sectionIndex,
            criterion,
            isFirstInSection: criterionIndex === 0,
            isLastInSection: criterionIndex === section.criteria.length - 1,
        })),
    )

    // -------------------------------------------------------------------
    // Contact column visibility — hide icons when no candidate has data
    // -------------------------------------------------------------------

    const hasAnyPhone = useMemo(
        () => candidates.some((c) => c.phone !== null && c.phone !== ''),
        [candidates],
    )
    const hasAnyEmail = useMemo(
        () => candidates.some((c) => c.email !== null && c.email !== ''),
        [candidates],
    )
    const hasAnyLinkedIn = useMemo(
        () =>
            candidates.some(
                (c) => c.linkedInUrl !== null && c.linkedInUrl !== '',
            ),
        [candidates],
    )
    const hasAnyContact = hasAnyPhone || hasAnyEmail || hasAnyLinkedIn

    // -------------------------------------------------------------------
    // Filter mutators
    // -------------------------------------------------------------------

    const setNameFilter = useCallback((value: string) => {
        setFilters((prev) => ({
            ...prev,
            name: value ? { value } : null,
        }))
    }, [])

    const clearNameFilter = useCallback(() => {
        setFilters((prev) => ({ ...prev, name: null }))
    }, [])

    const setScoreOperator = useCallback((operator: '>=' | '<=' | '=') => {
        setFilters((prev) => ({
            ...prev,
            score: { operator, value: prev.score?.value ?? 50 },
        }))
    }, [])

    const setScoreValue = useCallback((value: number) => {
        setFilters((prev) => ({
            ...prev,
            score: { operator: prev.score?.operator ?? '>=', value },
        }))
    }, [])

    const clearScoreFilter = useCallback(() => {
        setFilters((prev) => ({ ...prev, score: null }))
    }, [])

    const toggleCriterionValue = useCallback(
        (criterionId: string, value: string) => {
            setFilters((prev) => {
                const existing = prev.criteria[criterionId]
                const selected = new Set(existing?.selected ?? [])
                if (selected.has(value)) {
                    selected.delete(value)
                } else {
                    selected.add(value)
                }
                const newCriteria = { ...prev.criteria }
                if (selected.size === 0) {
                    delete newCriteria[criterionId]
                } else {
                    newCriteria[criterionId] = { selected }
                }
                return { ...prev, criteria: newCriteria }
            })
        },
        [],
    )

    const clearCriterionFilter = useCallback((criterionId: string) => {
        setFilters((prev) => {
            const newCriteria = { ...prev.criteria }
            delete newCriteria[criterionId]
            return { ...prev, criteria: newCriteria }
        })
    }, [])

    const clearAllFilters = useCallback(() => {
        setFilters(EMPTY_FILTERS)
    }, [])

    // -------------------------------------------------------------------
    // Determine if any filters are active
    // -------------------------------------------------------------------

    const hasAnyFilter =
        filters.name !== null ||
        filters.score !== null ||
        Object.keys(filters.criteria).length > 0

    // -------------------------------------------------------------------
    // Apply filters
    // -------------------------------------------------------------------

    const filteredCandidates = useMemo(() => {
        return candidates.filter((candidate) => {
            // Name filter
            if (filters.name) {
                const search = filters.name.value.toLowerCase()
                if (!candidate.name.toLowerCase().includes(search)) {
                    return false
                }
            }

            // Score filter
            if (filters.score) {
                const pct = calculateScorePercent(candidate, allCriteria)
                const { operator, value } = filters.score
                if (operator === '>=' && pct < value) return false
                if (operator === '<=' && pct > value) return false
                if (operator === '=' && pct !== value) return false
            }

            // Criteria filters
            for (const [criterionId, filter] of Object.entries(
                filters.criteria,
            )) {
                const entry = normaliseScore(candidate.scores[criterionId])
                const key = scoreValueToFilterKey(entry.value)
                if (!filter.selected.has(key)) return false
            }

            return true
        })
    }, [candidates, filters, allCriteria])

    // -------------------------------------------------------------------
    // Empty state
    // -------------------------------------------------------------------

    if (candidates.length === 0) {
        return (
            <div className="border border-gray-200 rounded-lg bg-white">
                <div className="text-center py-16 px-6">
                    <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                        <span className="text-xl text-gray-300">0</span>
                    </div>
                    <p className="text-base font-semibold text-gray-500">
                        No candidates screened yet
                    </p>
                    <p className="text-sm text-gray-400 mt-1.5 max-w-xs mx-auto">
                        Candidates will appear here after completing the
                        screener.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            {/* Active filters bar */}
            {hasAnyFilter && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50/50 border-b border-blue-100">
                    <span className="text-xs font-medium text-blue-600 shrink-0">
                        Filters:
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {filters.name && (
                            <FilterPill
                                label={`Name: "${filters.name.value}"`}
                                onClear={clearNameFilter}
                            />
                        )}
                        {filters.score && (
                            <FilterPill
                                label={`Score ${filters.score.operator} ${filters.score.value}%`}
                                onClear={clearScoreFilter}
                            />
                        )}
                        {Object.entries(filters.criteria).map(
                            ([criterionId, filter]) => {
                                const crit = allCriteria.find(
                                    (c) => c.criterion.id === criterionId,
                                )
                                const values = Array.from(filter.selected).join(
                                    ', ',
                                )
                                return (
                                    <FilterPill
                                        key={criterionId}
                                        label={`${crit?.criterion.label ?? criterionId}: ${values}`}
                                        onClear={() =>
                                            clearCriterionFilter(criterionId)
                                        }
                                    />
                                )
                            },
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={clearAllFilters}
                        className="ml-auto text-[11px] text-blue-500 hover:text-blue-700 font-medium transition-colors shrink-0"
                    >
                        Clear all
                    </button>
                </div>
            )}

            {/* No results after filtering */}
            {filteredCandidates.length === 0 && hasAnyFilter && (
                <div className="text-center py-12 px-6">
                    <p className="text-sm font-medium text-gray-500">
                        No candidates match the current filters
                    </p>
                    <button
                        type="button"
                        onClick={clearAllFilters}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    >
                        Clear all filters
                    </button>
                </div>
            )}

            {filteredCandidates.length > 0 && (
                <div>
                    <table className="w-full table-fixed border-collapse text-sm">
                        {/* ---------------------------------------------------------------
                            TWO-ROW HEADER
                            Row 1: Sticky candidate columns (rowSpan=2) + section groups
                            Row 2: Individual criterion sub-headers
                        --------------------------------------------------------------- */}
                        <thead>
                            {/* --- Row 1: section group headers --- */}
                            <tr className="border-b border-gray-200">
                                {/* Avatar - sticky */}
                                <th
                                    rowSpan={2}
                                    className="bg-gray-50 w-12 border-b border-gray-200"
                                />

                                {/* Name - sticky */}
                                <th
                                    rowSpan={2}
                                    className="group/th bg-gray-50 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200"
                                >
                                    <div className="flex items-center gap-1.5">
                                        Name
                                        <ColumnFilter
                                            kind="text"
                                            isActive={filters.name !== null}
                                            value={filters.name?.value ?? ''}
                                            onChange={setNameFilter}
                                            onClear={clearNameFilter}
                                        />
                                    </div>
                                </th>

                                {/* Contact - single merged column (hidden if no candidate has contact data) */}
                                {hasAnyContact && (
                                    <th
                                        rowSpan={2}
                                        className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50"
                                    >
                                        Contact
                                    </th>
                                )}

                                {/* Score - total percentage */}
                                <th
                                    rowSpan={2}
                                    className="group/th px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50"
                                >
                                    <div className="flex items-center justify-center gap-1.5">
                                        Score
                                        <ColumnFilter
                                            kind="score"
                                            isActive={filters.score !== null}
                                            operator={
                                                filters.score?.operator ?? '>='
                                            }
                                            value={filters.score?.value ?? 50}
                                            onOperatorChange={setScoreOperator}
                                            onValueChange={setScoreValue}
                                            onClear={clearScoreFilter}
                                        />
                                    </div>
                                </th>

                                {/* Section group headers - colour coded */}
                                {rubric.sections.map((section, idx) => {
                                    const accent =
                                        SECTION_ACCENTS[
                                            idx % SECTION_ACCENTS.length
                                        ]
                                    return (
                                        <th
                                            key={section.id}
                                            colSpan={section.criteria.length}
                                            className={clsx(
                                                accent.headerBg,
                                                'text-center text-[11px] font-semibold uppercase tracking-wider text-gray-600',
                                                'px-2 py-2.5',
                                                'border-b border-gray-200',
                                                'border-l-2',
                                                accent.borderColor,
                                            )}
                                        >
                                            {section.label}
                                        </th>
                                    )
                                })}
                            </tr>

                            {/* --- Row 2: criterion sub-headers --- */}
                            <tr className="border-b border-gray-100">
                                {allCriteria.map(
                                    ({
                                        criterion,
                                        sectionIndex,
                                        isFirstInSection,
                                    }) => {
                                        const accent =
                                            SECTION_ACCENTS[
                                                sectionIndex %
                                                    SECTION_ACCENTS.length
                                            ]
                                        const criterionFilterActive =
                                            criterion.id in filters.criteria
                                        return (
                                            <th
                                                key={criterion.id}
                                                className={clsx(
                                                    'group/th text-[11px] font-medium text-gray-500 text-center',
                                                    'px-3 py-2',
                                                    'bg-gray-50/50',
                                                    isFirstInSection &&
                                                        'border-l-2',
                                                    isFirstInSection &&
                                                        accent.borderColor,
                                                )}
                                                title={criterion.label}
                                            >
                                                <div className="flex items-center justify-center gap-1">
                                                    {criterion.shortName ||
                                                        (criterion.label
                                                            .length > 15
                                                            ? criterion.label.slice(
                                                                  0,
                                                                  15,
                                                              ) + '...'
                                                            : criterion.label)}
                                                    <ColumnFilter
                                                        kind="discrete"
                                                        isActive={
                                                            criterionFilterActive
                                                        }
                                                        options={optionsForScoreType(
                                                            criterion.scoreType,
                                                        )}
                                                        selected={
                                                            filters.criteria[
                                                                criterion.id
                                                            ]?.selected ??
                                                            new Set()
                                                        }
                                                        onToggle={(v) =>
                                                            toggleCriterionValue(
                                                                criterion.id,
                                                                v,
                                                            )
                                                        }
                                                        onClear={() =>
                                                            clearCriterionFilter(
                                                                criterion.id,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </th>
                                        )
                                    },
                                )}
                            </tr>
                        </thead>

                        {/* ---------------------------------------------------------------
                            CANDIDATE ROWS
                        --------------------------------------------------------------- */}
                        <tbody>
                            {[...filteredCandidates]
                                .sort(
                                    (a, b) =>
                                        calculateScorePercent(b, allCriteria) -
                                        calculateScorePercent(a, allCriteria),
                                )
                                .map((candidate, rowIdx) => {
                                    const scorePercent = calculateScorePercent(
                                        candidate,
                                        allCriteria,
                                    )
                                    const highlightClass =
                                        scorePercent >= 75 ? 'bg-green-50' : ''
                                    return (
                                        <tr
                                            key={candidate.id}
                                            className={clsx(
                                                'border-b border-gray-100 last:border-b-0',
                                                'hover:bg-blue-50/30 transition-colors',
                                                rowIdx % 2 === 1 &&
                                                    !highlightClass &&
                                                    'bg-gray-50/30',
                                            )}
                                        >
                                            {/* Avatar - sticky */}
                                            <td
                                                className={clsx(
                                                    'px-3 py-2.5 w-12',
                                                    highlightClass,
                                                )}
                                            >
                                                {candidate.profilePicUrl ? (
                                                    <img
                                                        src={
                                                            candidate.profilePicUrl
                                                        }
                                                        alt={candidate.name}
                                                        className="w-8 h-8 rounded-full object-cover ring-1 ring-gray-200"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-ctxt-blue/10 text-ctxt-blue flex items-center justify-center text-[11px] font-bold">
                                                        {candidate.name
                                                            .split(' ')
                                                            .map((n) => n[0])
                                                            .join('')
                                                            .slice(0, 2)}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Name - sticky */}
                                            <td
                                                className={clsx(
                                                    'px-3 py-2.5',
                                                    highlightClass,
                                                )}
                                            >
                                                <span className="font-semibold text-sm text-gray-900">
                                                    {candidate.name}
                                                </span>
                                            </td>

                                            {/* Contact - compact single cell (hidden if no candidate has contact data) */}
                                            {hasAnyContact && (
                                                <td
                                                    className={clsx(
                                                        'px-3 py-2.5',
                                                        highlightClass,
                                                    )}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {hasAnyPhone &&
                                                            candidate.phone && (
                                                                <a
                                                                    href={`tel:${candidate.phone}`}
                                                                    className="text-gray-400 hover:text-ctxt-blue transition-colors"
                                                                    title={
                                                                        candidate.phone
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faPhone
                                                                        }
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                </a>
                                                            )}
                                                        {hasAnyEmail &&
                                                            candidate.email && (
                                                                <a
                                                                    href={`mailto:${candidate.email}`}
                                                                    className="text-gray-400 hover:text-ctxt-blue transition-colors"
                                                                    title={
                                                                        candidate.email
                                                                    }
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faEnvelope
                                                                        }
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                </a>
                                                            )}
                                                        {hasAnyLinkedIn &&
                                                            candidate.linkedInUrl && (
                                                                <a
                                                                    href={
                                                                        candidate.linkedInUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-gray-400 hover:text-[#0A66C2] transition-colors"
                                                                    title="LinkedIn profile"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faLinkedin
                                                                        }
                                                                        className="w-3.5 h-3.5"
                                                                    />
                                                                </a>
                                                            )}
                                                    </div>
                                                </td>
                                            )}

                                            {/* Score % */}
                                            <td
                                                className={clsx(
                                                    'px-3 py-2.5 text-center',
                                                    highlightClass,
                                                )}
                                            >
                                                <span className="font-semibold text-sm text-gray-900">
                                                    {scorePercent}%
                                                </span>
                                            </td>

                                            {/* Score cells */}
                                            {allCriteria.map(
                                                ({
                                                    criterion,
                                                    sectionIndex,
                                                    isFirstInSection,
                                                }) => {
                                                    const accent =
                                                        SECTION_ACCENTS[
                                                            sectionIndex %
                                                                SECTION_ACCENTS.length
                                                        ]

                                                    const entry =
                                                        normaliseScore(
                                                            candidate.scores[
                                                                criterion.id
                                                            ],
                                                        )

                                                    return (
                                                        <td
                                                            key={criterion.id}
                                                            className={clsx(
                                                                'px-3 py-2.5 text-center',
                                                                isFirstInSection &&
                                                                    'border-l-2',
                                                                isFirstInSection &&
                                                                    accent.borderColor,
                                                            )}
                                                        >
                                                            <div className="flex flex-col items-center gap-0.5">
                                                                <RubricScoreBadge
                                                                    scoreType={
                                                                        criterion.scoreType
                                                                    }
                                                                    value={
                                                                        entry.value
                                                                    }
                                                                />
                                                                {entry.detail && (
                                                                    <span className="text-[10px] text-gray-500 leading-tight">
                                                                        {
                                                                            entry.detail
                                                                        }
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )
                                                },
                                            )}
                                        </tr>
                                    )
                                })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

// ---------------------------------------------------------------------------
// FilterPill - small tag showing an active filter in the filter bar
// ---------------------------------------------------------------------------

function FilterPill({
    label,
    onClear,
}: {
    label: string
    onClear: () => void
}): React.JSX.Element {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 px-2 py-0.5 text-[11px] text-blue-700 font-medium">
            <span className="max-w-[160px] truncate">{label}</span>
            <button
                type="button"
                onClick={onClear}
                className="hover:text-red-500 transition-colors"
                aria-label={`Remove filter: ${label}`}
            >
                <X className="w-3 h-3" />
            </button>
        </span>
    )
}
